/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Injectable } from '@angular/core';
import { from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { isEmailAddress, readPeopleCsv } from '../../helpers/admin-enrolment.helper';
import {
  AdminEnrolment,
  AdminEnrolmentPerson,
  AdminEnrolmentRequest,
  AdminEnrolmentResult,
} from '../../models/playlist/admin-enrolment.model';
import { ObservableResult } from '../../store';
import { AdminEnrolmentsDataService } from './admin-enrolments-data.service';

const SEAT_LIMIT = 120;
const SEATS_TAKEN = 118;

/** Addresses with `new` in them have no account, `outside` aren't in the audience and `waiting` are on the waiting list. */
@Injectable()
export class MockAdminEnrolmentsDataService implements AdminEnrolmentsDataService {
  enrol(playlistUid: string, request: AdminEnrolmentRequest): ObservableResult<AdminEnrolment> {
    const people = [
      ...request.userUids.map((userUid): AdminEnrolmentResult => enrolled({ userUid, email: null })),
      ...request.people.map((person) => result(person)),
    ];
    return ObservableResult.ofSuccess(enrolment(people, false));
  }

  importCsv(playlistUid: string, file: File, options: { preview: boolean; notify: boolean }): ObservableResult<AdminEnrolment> {
    return from(file.text()).pipe(
      switchMap((text) => {
        const people = [...readPeopleCsv(text).values()].map((person, index) => ({ ...result(person), row: index + 1 }));
        return ObservableResult.ofSuccess(enrolment(people, options.preview));
      }),
    );
  }

  previewPerson(playlistUid: string, person: AdminEnrolmentPerson): ObservableResult<AdminEnrolment> {
    return ObservableResult.ofSuccess(enrolment([result(person)], true));
  }
}

function result(person: AdminEnrolmentPerson): AdminEnrolmentResult {
  const email = person.email.toLowerCase();
  if (!isEmailAddress(email)) {
    return {
      userUid: null,
      email: person.email,
      outcome: 'NOT_ENROLLED',
      problem: 'INVALID_EMAIL',
      newAccount: false,
      outsideAudience: false,
    };
  }
  const newAccount = email.includes('new');
  if (newAccount && (!person.firstName || !person.lastName)) {
    return {
      userUid: null,
      email: person.email,
      outcome: 'NOT_ENROLLED',
      problem: 'MISSING_NAME',
      newAccount: false,
      outsideAudience: false,
    };
  }
  return {
    ...enrolled({ userUid: newAccount ? null : `user-${email}`, email: person.email }),
    newAccount,
    outsideAudience: email.includes('outside'),
    previousStatus: email.includes('waiting') ? 'WAITLISTED' : null,
  };
}

function enrolled(asked: Pick<AdminEnrolmentResult, 'userUid' | 'email'>): AdminEnrolmentResult {
  return { ...asked, outcome: 'ENROLLED', newAccount: false, outsideAudience: false };
}

function enrolment(people: AdminEnrolmentResult[], preview: boolean): AdminEnrolment {
  const taken = people.filter((person) => person.outcome === 'ENROLLED').length;
  return { people, preview, seatLimit: SEAT_LIMIT, seatsTaken: SEATS_TAKEN + taken };
}
